/**
 * Typed wrappers for the admin groups CRUD endpoints. The server
 * controller lives at `apps/server/src/players/groups.controller.ts`
 * and exposes:
 *
 *   GET    /api/v1/admin/groups/tree
 *   POST   /api/v1/admin/groups            -> 201 PlayerGroupSummary
 *   PATCH  /api/v1/admin/groups/:id        -> 200 PlayerGroupSummary
 *   DELETE /api/v1/admin/groups/:id        -> 204 No Content
 *
 * `listGroupsTree` already lives in `api/players.ts` (Task 28) — we
 * re-export it here so callers can import everything related to groups
 * from one module. The Task-28 import path keeps working.
 */

import type {
  GroupKind,
  NameI18n,
  PlayerGroupCreate,
  PlayerGroupSummary,
} from '@displaycms/contracts';

import { apiDelete, apiPatch, apiPost } from '@/api/client';

export { listGroupsTree, flattenGroupsTree } from '@/api/players';
export type { GroupsTreeResponse, FlatGroupOption } from '@/api/players';

export interface CreateGroupInput {
  parent_id: string | null;
  name_i18n: NameI18n;
  /** Screen-inventory (SP2): the group's semantic level. Server defaults to 'zone'. */
  kind?: GroupKind | undefined;
}

export function createGroup(input: CreateGroupInput): Promise<PlayerGroupSummary> {
  const body: PlayerGroupCreate = {
    parent_id: input.parent_id,
    name_i18n: input.name_i18n,
    ...(input.kind !== undefined ? { kind: input.kind } : {}),
  };
  return apiPost<PlayerGroupSummary>('/v1/admin/groups', body);
}

export interface UpdateGroupInput {
  parent_id?: string | null | undefined;
  name_i18n?: NameI18n | undefined;
  kind?: GroupKind | undefined;
}

export function updateGroup(
  id: string,
  patch: UpdateGroupInput,
): Promise<PlayerGroupSummary> {
  const body: Record<string, unknown> = {};
  if (patch.parent_id !== undefined) body['parent_id'] = patch.parent_id;
  if (patch.name_i18n !== undefined) body['name_i18n'] = patch.name_i18n;
  if (patch.kind !== undefined) body['kind'] = patch.kind;
  return apiPatch<PlayerGroupSummary>(`/v1/admin/groups/${id}`, body);
}

export function deleteGroup(id: string): Promise<void> {
  return apiDelete<void>(`/v1/admin/groups/${id}`);
}
